import cn from "classnames"
import React, { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { isMobile } from "../util"
import styles from "./sign_preview.module.css"
import SignSVGInline from "./sign_svg_inline"
import handComparison from "@/images/layout/preview-hand-comparison.png"
import humanComparison from "@/images/layout/preview-human-comparison.png"
import roomLarge from "@/images/layout/preview-room-large.png"
import roomSmall from "@/images/layout/preview-room-small.png"
import type Sign from "@/signomatic/app/sign"
import { ChevronLeftLarge, ChevronRightLarge, CloseLarge } from "@/skylticons"

export type PreviewComparisonType = "human" | "room"

interface PreviewSlideType {
  title?: string | null
  subTitle?: string | null
}

export interface PreviewSvgSlide extends PreviewSlideType {
  type: "svg"
  svg: string
}

export interface PreviewMinimaticSlide extends PreviewSlideType {
  type: "minimatic"
  svg: string
  sign?: Sign
  loadingFailed?: boolean
}

export interface PreviewImageSlide extends PreviewSlideType {
  type: "image"
  webp?: string | null
  webp2x?: string | null
  png?: string | null
  png2x?: string | null
  altText?: string | null
}

export interface PreviewShoppingSlide extends PreviewSlideType {
  type: "shopping"
  webp?: string | null
  webp2x?: string | null
  png?: string | null
  png2x?: string | null
  altText?: string | null
}

export interface PreviewComparisonSlide extends PreviewSlideType {
  type: "comparison"
  comparison: PreviewComparisonType
  svg: string
  width: number
  height: number
}

export type PreviewSlide =
  | PreviewSvgSlide
  | PreviewMinimaticSlide
  | PreviewImageSlide
  | PreviewShoppingSlide
  | PreviewComparisonSlide

const bigComparison = (width: number, height: number): boolean => width > 340 || height > 340

export const comparisonImage = (slide: PreviewComparisonSlide): string => {
  const big = bigComparison(slide.width, slide.height)

  switch (slide.comparison) {
    case "human":
      return big ? humanComparison : handComparison
    case "room":
      return big ? roomLarge : roomSmall
  }
}

const reducedHeight = (width: number, height: number, type: PreviewComparisonType) => {
  const big = bigComparison(width, height)

  const roomHeightMm = big ? 3022 : 1278
  const humanHeightMm = big ? 1800 : 326
  const comparisonHeight = type === "room" ? roomHeightMm : humanHeightMm

  return ((height || 0) / comparisonHeight) * 100 > (isMobile() ? 40 : 70) && type != "room" ? 40 : 100
}

interface PreviewComparisonProps {
  svg: string
  width: number
  height: number
  type: PreviewComparisonType
}

const PreviewComparisonLarge: React.FC<PreviewComparisonProps> = ({ svg, width, height, type }) => {
  const big = bigComparison(width, height)

  // calculating sign height based on background height
  const backgroundMargin = window.innerWidth > 767 ? 192 : 120
  const backgroundHeightPx = Math.min(1000, window.innerHeight - backgroundMargin)
  const roomHeightMm = big ? 3022 : 1278
  const humanHeightMm = big ? 1800 : 326
  const comparisonHeight = type === "room" ? roomHeightMm : humanHeightMm
  const signHeightPrc = `${((height || 0) / comparisonHeight) * 100}%`

  const previewClass = `${type}-${big ? "large" : "small"}`

  return (
    <span className="w-full" style={{ height: backgroundHeightPx }}>
      <div className="w-full" style={{ height: `${reducedHeight(width, height, type)}%` }}>
        <SignSVGInline
          component="div"
          svg={svg}
          height={signHeightPrc}
          className={cn("preview", styles["svg"], styles[type], styles[previewClass], "data-hj-suppress")}
        />
      </div>
    </span>
  )
}
export const PreviewComparisonSmall: React.FC<PreviewComparisonProps> = ({ svg, width, height, type }) => {
  const big = bigComparison(width, height)

  // calculating sign height based on background height
  const roomHeightMm = big ? 3022 : 1278
  const humanHeightMm = big ? 1800 : 326
  const comparisonHeight = type === "room" ? roomHeightMm : humanHeightMm
  const signHeightPrc = `${((height || 0) / comparisonHeight) * 100}%`

  const previewClass = `${type}-${big ? "large" : "small"}`

  return (
    <SignSVGInline
      component="div"
      svg={svg}
      height={signHeightPrc}
      className={cn(
        "preview data-hj-suppress flex h-full w-full items-center justify-center",
        styles["svg"],
        styles[type],
        styles[previewClass]
      )}
    />
  )
}

interface PreviewModalProps {
  slides: PreviewSlide[]
  initialSlide?: number
  close: () => void
}

export const PreviewModal: React.FC<PreviewModalProps> = ({ slides, initialSlide, close }) => {
  const { t } = useTranslation()
  const [currentSlide, setCurrentSlide] = useState(initialSlide || 0)
  const slideBtnsWrapper = useRef<HTMLDivElement>(null)

  const slide = slides[currentSlide]
  const slidesCount = slides.length

  const changeSlide = (newSlide: number) => {
    let adapted
    if (newSlide < 0) {
      adapted = slidesCount - 1
    } else if (newSlide === slidesCount) {
      adapted = 0
    } else {
      adapted = newSlide
    }

    setCurrentSlide(adapted)

    const slideBtn = slideBtnsWrapper.current?.children[adapted]
    if (slideBtn) {
      slideBtn.scrollIntoView({ behavior: "smooth" })
    }
  }

  let title = ""
  let subTitle = ""

  if (slide.type === "comparison") {
    const big = bigComparison(slide.width, slide.height)
    title = t("preview.size_comparison")

    switch (slide.comparison) {
      case "room":
        subTitle = t("preview.comparison_room")
        break
      case "human":
        subTitle = big ? t("preview.comparison_human") : t("preview.comparison_hand")
        break
    }
  }

  if (slide.title) {
    title = slide.title
  }

  if (slide.subTitle) {
    subTitle = slide.subTitle
  }

  return createPortal(
    <div
      className={cn(
        "z-40",
        styles["signomatic-modal"],
        styles["preview-modal"],
        "signomatic-modal",
        "preview-modal",
        styles["transparent"]
      )}
    >
      <div className={styles["preview-background"]} onClick={close} />
      <div className={styles["preview"]}>
        <div className={styles["top-bar"]}>
          <div className={cn(styles["preview-info"], "truncate")}>
            <span className="truncate text-base font-semibold text-black">{title}</span>
            <span className="text-dark truncate text-sm">{subTitle}</span>
          </div>
          <button
            className={cn(
              "button button-subtle preview-close mb-0 inline-flex h-10 flex-shrink-0 items-center justify-center rounded px-4 hover:bg-secondary-light",
              styles["preview-close"]
            )}
            onClick={close}
            type="button"
          >
            <CloseLarge className="h-6 w-6" />
            <p className="mb-0 text-base font-semibold text-black">{t("misc.close")}</p>
          </button>
        </div>

        <div className={styles["preview-main"]}>
          <div className={styles["preview-slideshow"]}>
            {slidesCount > 1 && (
              <div className={cn("z-10", styles["slideshow-buttons"])}>
                <div
                  className={cn(styles["slide-left"], "button button-large button-secondary flat cursor-pointer")}
                  onClick={() => changeSlide(currentSlide - 1)}
                >
                  <ChevronLeftLarge />
                </div>
                <div
                  className={cn(styles["slide-right"], "button button-large button-secondary flat cursor-pointer")}
                  onClick={() => changeSlide(currentSlide + 1)}
                >
                  <ChevronRightLarge />
                </div>
              </div>
            )}
            <div
              className={styles["slideshow-contents"]}
              style={{
                height:
                  slide.type === "comparison"
                    ? `${reducedHeight(slide.width, slide.height, slide.comparison)}%`
                    : "100%",
              }}
            >
              <div
                className={styles["slide"]}
                style={{ width: `${slidesCount * 100}%`, height: "100%", left: `${currentSlide * -100}%` }}
              >
                <div className={styles["background-wrapper"]}>
                  {slides.map((previewSlide, i) => {
                    return (
                      <div className={styles["img-wrapper"]} key={i}>
                        {(previewSlide.type === "svg" || previewSlide.type === "minimatic") && (
                          <SignSVGInline
                            component="div"
                            svg={previewSlide.svg}
                            className={cn(styles["svg"], "data-hj-suppress")}
                          />
                        )}
                        {(previewSlide.type === "shopping" || previewSlide.type === "image") && previewSlide.png && (
                          <picture className="flex h-full w-full items-center justify-center">
                            {previewSlide.webp && (
                              <source srcSet={previewSlide.webp2x || previewSlide.webp} type="image/webp" />
                            )}
                            <img src={previewSlide.png2x || previewSlide.png} alt={previewSlide.altText || ""} />
                          </picture>
                        )}
                        {previewSlide.type === "comparison" && (
                          <img
                            id={`comparison-img-${i}`}
                            className={cn("max-w-none", {
                              "ml-[160px] md:ml-[400px]": previewSlide.comparison === "human",
                            })}
                            src={comparisonImage(previewSlide)}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
              {slide.type === "comparison" && (
                <div className={cn(styles["svg-wrapper"], styles["svg-with-background"])}>
                  <PreviewComparisonLarge
                    svg={slide.svg}
                    width={slide.width}
                    height={slide.height}
                    type={slide.comparison}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles["bottom-bar"]}>
            {slidesCount > 1 && (
              <>
                <div
                  className={cn(styles["slide-left"], "button button-large button-secondary flat cursor-pointer")}
                  onClick={() => changeSlide(currentSlide - 1)}
                >
                  <ChevronLeftLarge />
                </div>
                <div className={cn(styles["thumbnail-wrapper"], "m-auto")} ref={slideBtnsWrapper}>
                  {slides.map((thumbSlide, i) => (
                    <button
                      key={i}
                      id={`slide-btn-${i}`}
                      className={cn(
                        "scroll-mx-6 scroll-px-6",
                        styles["thumbnail"],
                        i === currentSlide ? styles["active"] : "",
                        thumbSlide.type === "comparison" && thumbSlide.comparison === "room" ? styles["room"] : "",
                        thumbSlide.type === "shopping" || thumbSlide.type === "image" ? styles["extra-image"] : ""
                      )}
                      onClick={e => {
                        e.preventDefault()
                        changeSlide(i)
                      }}
                      type="button"
                    >
                      {(thumbSlide.type === "svg" || thumbSlide.type === "minimatic") && (
                        <SignSVGInline svg={thumbSlide.svg} className={(styles["svg"], "data-hj-suppress")} />
                      )}
                      {(thumbSlide.type === "shopping" || thumbSlide.type === "image") && thumbSlide.png && (
                        <img src={thumbSlide.png} />
                      )}
                      {thumbSlide.type === "comparison" && <img src={comparisonImage(thumbSlide)} alt="" />}
                    </button>
                  ))}
                </div>
                <div
                  className={cn(styles["slide-right"], "button button-large button-secondary flat cursor-pointer")}
                  onClick={() => changeSlide(currentSlide + 1)}
                >
                  <ChevronRightLarge />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default PreviewModal
